import React, { useEffect } from 'react';
import { useWizard } from '../../context/WizardContext';
import NavigationButtons from '../shared/NavigationButtons';
import { FileText, Info, RefreshCw } from 'lucide-react';

function D3_ProjectSynopsis() {
  const { dataE, dataA, updateDataRaw, nextStep, previousStep } = useWizard();
  const synopsis = dataE.projectSynopsis;

  const updateSynopsis = (field, value) => {
    updateDataRaw({
      'wizardE.projectSynopsis': { ...synopsis, [field]: value }
    });
  };

  const importFromWizardA = () => {
    const aim = dataA.intake?.aim || '';
    const methodology = dataA.methodologicalChoice || '';
    const strategy = dataA.strategy || '';
    const techniques = dataA.techniques?.dataCollection || [];
    
    let methodologyText = '';
    if (methodology) {
      methodologyText += `Methodological Approach: ${methodology}\n`;
    }
    if (strategy) {
      methodologyText += `Research Strategy: ${strategy}\n`;
    }
    if (techniques.length > 0) {
      methodologyText += `Data Collection Methods: ${techniques.join(', ')}\n`;
    }
    if (dataA.techniques?.sampling) {
      const samplingStrategies = Array.isArray(dataA.techniques.sampling) 
        ? dataA.techniques.sampling.join(', ')
        : dataA.techniques.sampling;
      methodologyText += `Sampling Strategy: ${samplingStrategies}\n`;
    }
    
    updateDataRaw({
      'wizardE.projectSynopsis': {
        ...synopsis,
        researchQuestions: aim || synopsis.researchQuestions,
        methodology: methodologyText || synopsis.methodology
      }
    });
  };

  const hasWizardAData = dataA.intake?.aim || dataA.methodologicalChoice || 
                         (dataA.techniques?.dataCollection?.length > 0);

  const isComplete = synopsis.title && synopsis.summary && synopsis.researchQuestions;

  return (
    <div className="wizard-step">
      <div className="step-header">
        <FileText className="step-icon" size={32} />
        <div>
          <h2>Step 3: Project Synopsis</h2>
          <p className="step-description">
            Provide an overview of your research project including its aims, timeline, and methodology.
          </p>
        </div>
      </div>

      {hasWizardAData && (
        <div className="info-box success">
          <RefreshCw size={20} />
          <div>
            <strong>Import from Methodology Wizard</strong>
            <p>
              We detected data from Wizard A. You can auto-fill some fields based on your previous responses.
            </p>
            <button className="btn-secondary btn-sm" onClick={importFromWizardA}>
              Import Research Questions & Methodology
            </button>
          </div>
        </div>
      )}

      <div className="form-section">
        <h3>Project Details</h3>

        <div className="form-group">
          <label>Project Title *</label>
          <input
            type="text"
            value={synopsis.title}
            onChange={(e) => updateSynopsis('title', e.target.value)}
            placeholder="Enter the full title of your research project"
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Project Start Date</label>
            <input
              type="date"
              value={synopsis.startDate}
              onChange={(e) => updateSynopsis('startDate', e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Project End Date</label>
            <input
              type="date"
              value={synopsis.endDate}
              onChange={(e) => updateSynopsis('endDate', e.target.value)}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Ethics Approval Start Date</label>
            <input
              type="date"
              value={synopsis.ethicsStartDate}
              onChange={(e) => updateSynopsis('ethicsStartDate', e.target.value)}
            />
            <small className="field-help">When do you need ethics approval to begin?</small>
          </div>
          <div className="form-group">
            <label>Ethics Approval End Date</label>
            <input
              type="date"
              value={synopsis.ethicsEndDate}
              onChange={(e) => updateSynopsis('ethicsEndDate', e.target.value)}
            />
            <small className="field-help">When will data collection be complete?</small>
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>Funding</h3>

        <div className="form-group">
          <label>Funding Source</label>
          <select
            value={synopsis.fundingSource}
            onChange={(e) => updateSynopsis('fundingSource', e.target.value)}
          >
            <option value="">Select funding source...</option>
            <option value="internal">Internal / Institutional Funding</option>
            <option value="external_grant">External Research Grant</option>
            <option value="industry">Industry / Commercial Sponsor</option>
            <option value="government">Government Funding</option>
            <option value="charity">Charity / Foundation</option>
            <option value="self_funded">Self-Funded</option>
            <option value="none">No Funding Required</option>
            <option value="other">Other</option>
          </select>
        </div>

        {synopsis.fundingSource && synopsis.fundingSource !== 'none' && (
          <div className="form-group">
            <label>Funding Details</label>
            <textarea
              value={synopsis.fundingDetails}
              onChange={(e) => updateSynopsis('fundingDetails', e.target.value)}
              placeholder="Provide details about the funding source, grant number, or sponsor"
              rows={3}
            />
          </div>
        )}
      </div>

      <div className="form-section">
        <h3>Research Overview</h3>

        <div className="form-group">
          <label>Project Summary * (Lay Summary)</label>
          <textarea
            value={synopsis.summary}
            onChange={(e) => updateSynopsis('summary', e.target.value)}
            placeholder="Provide a brief, non-technical summary of your research that could be understood by a non-specialist (approx. 300 words)"
            rows={6}
          />
          <small className="field-help">
            This should explain what you are studying, why it matters, and what you hope to find.
          </small>
        </div>

        <div className="form-group">
          <label>Research Questions / Objectives *</label>
          <textarea
            value={synopsis.researchQuestions}
            onChange={(e) => updateSynopsis('researchQuestions', e.target.value)}
            placeholder="List your main research questions or objectives"
            rows={4}
          />
        </div>

        <div className="form-group">
          <label>Methodology Overview</label>
          <textarea
            value={synopsis.methodology}
            onChange={(e) => updateSynopsis('methodology', e.target.value)}
            placeholder="Briefly describe the methodological approach, including research design, data collection methods, and analysis approach"
            rows={6}
          />
          <small className="field-help">
            Include your research design, sampling strategy, data collection methods, and planned analysis.
          </small>
        </div>
      </div>

      <NavigationButtons
        onPrevious={previousStep}
        onNext={isComplete ? nextStep : null}
        disableNext={!isComplete}
        nextLabel="Continue to Participants"
      />
    </div>
  );
}

export default D3_ProjectSynopsis;
